import React, { useContext, useEffect, useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Container,
  Grid2 as Grid,
  Grid2Props as GridProps,
} from "@mui/material";

import {
  ActionIcon,
  Badge,
  Button,
  Card,
  CopyButton,
  Divider,
  Group,
  Modal,
  Table,
  Tabs,
  Text,
  TextProps,
  Tooltip,
  Avatar,
  Stack,
  ScrollArea,
  Select,
} from "@mantine/core";

import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronRight,
  IconCopy,
  IconEye,
  IconFiles,
  IconNotebook,
  IconPlayerPlay,
  IconTerminal2,
  IconUserCircle,
  IconUsers,
  IconChartBar,
  IconClipboardCheck,
  IconVideo,
  IconPlus,
  IconReload,
  IconCheck,
  IconX,
  IconUpload,
  IconPhoto,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Colors, HttpErrorCodes, HttpSuccessCodesObj } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getEllipsisFromString,
} from "../../Lib/methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import {
  AssessmentProps,
  AttemptProps,
  CourseProps,
  LessonProps,
  PaginationProps,
  RecordingProps,
  StudentProfileProps,
} from "../../Lib/types";
import {
  PerformRequest,
  UploadFiles,
  usePerformRequest,
} from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";
import "./styles.scss";
import DatagridDisplay from "../../Components/DatagridDisplay";
import { GridRenderCellParams } from "@mui/x-data-grid";
import StudentPerformance from "../../Components/StudentPerformance";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
const SingleCourse: React.FC = () => {
  const context = useContext(AppContext);
  const { courseID } = useParams();
  const navigate = useNavigate();

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 10,
  });
  const {
    data: course,
    isLoading: isLoadingCourse,
    reloadData: reloadCourse,
    response: courseAxiosResponse,
  } = usePerformRequest<NonPaginatedResponse<CourseProps>>({
    route: Endpoints.GetSingleCourseDetails(courseID as string).route,
    method: Endpoints.GetSingleCourseDetails(courseID as string).method,
  });

  const {
    data: lessons,
    isLoading: isLoadingLessons,
    reloadData: reloadLessons,
    response: lessonsAxiosResponse,
  } = usePerformRequest<PaginatedResponse<LessonProps[]>>({
    route: Endpoints.GetCourseLessons(
      courseID as string,
      pagination.page,
      pagination.limit
    ).route,
    method: Endpoints.GetCourseLessons(
      courseID as string,
      pagination.page,
      pagination.limit
    ).method,
  });

  const {
    data: students,
    isLoading: isLoadingStudents,
    reloadData: reloadStudents,
    response: studentsAxiosResponse,
  } = usePerformRequest<PaginatedResponse<StudentProfileProps[]>>({
    route: Endpoints.GeteStudentsInCourse(
      courseID as string,
      pagination.page,
      pagination.limit
    ).route,
    method: Endpoints.GeteStudentsInCourse(
      courseID as string,
      pagination.page,
      pagination.limit
    ).method,
  });

  const [
    isLessonModalOpen,
    { close: CloseLessonModal, open: OpenLessonModal },
  ] = useDisclosure(false);

  const [currentLesson, setCurrentLesson] = useState<LessonProps>();

  const [
    isStudentModalOpen,
    { close: closeStudentModal, open: openStudentModal },
  ] = useDisclosure(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentProfileProps>();
  const [studentAssessmentHistory, setStudentAssessmentHistory] = useState<{
    assessments: Omit<AssessmentProps, "questions">[];
    attempts: AttemptProps[];
  }>();

  const [
    isLoadingStudentAssessmentHistory,
    setLoadingStudentAssessmentHistory,
  ] = useState(false);

  const [
    isAssessmentHistoryModalOpen,
    { close: closeAssessmentHistoryModal, open: openAssessmentHistoryModal },
  ] = useDisclosure(false);

  const [
    isStudentPerformanceModalOpen,
    { close: closeStudentPerformanceModal, open: openStudentPerformanceModal },
  ] = useDisclosure(false);

  const GetStudentAssessmentHistory = async () => {
    setLoadingStudentAssessmentHistory(true);
    const r = await PerformRequest<
      NonPaginatedResponse<{
        assessments: AssessmentProps[];
        attempts: AttemptProps[];
      }>
    >({
      route: Endpoints.GetStudentAssessmentHistory(
        selectedStudent?.id as string
      ).route,
      method: Endpoints.GetStudentAssessmentHistory(
        selectedStudent?.id as string
      ).method,
      callback: () => {
        setLoadingStudentAssessmentHistory(false);
      },
    });
    setLoadingStudentAssessmentHistory(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        setStudentAssessmentHistory(r?.data?.data);
      } else {
        DefaultErrorNotification(
          r?.data?.message ?? "Could not get student assessment history!"
        );
      }
    }
  };
  useEffect(() => {
    if (courseAxiosResponse) {
      if (HttpErrorCodes.includes(courseAxiosResponse.status)) {
        DefaultErrorNotification("Could not get course details!");
        navigate("/d/courses");
      }
    }
  }, [isLoadingCourse, courseAxiosResponse]);

  useEffect(() => {
    if (isAssessmentHistoryModalOpen && selectedStudent) {
      GetStudentAssessmentHistory();
    }
  }, [isAssessmentHistoryModalOpen, selectedStudent]);

  const [recordingsPagination, setRecordingsPagination] =
    useState<PaginationProps>({
      page: 1,
      limit: 20,
    });
  const {
    data: recordings,
    isLoading: isLoadingRecordings,
    reloadData: reloadRecordings,
    response: recordingsAxiosResponse,
  } = usePerformRequest<PaginatedResponse<RecordingProps[]>>({
    route: Endpoints.GetRecordingsUnderCourse(
      courseID as string,
      recordingsPagination.page,
      recordingsPagination.limit
    ).route,
    method: Endpoints.GetRecordingsUnderCourse(
      courseID as string,
      recordingsPagination.page,
      recordingsPagination.limit
    ).method,
  });

  const [isRecordingModalOpen, setIsRecordingModalOpen] =
    useState<boolean>(false);
  const [selectedRecordingFiles, setSelectedRecordingFiles] = useState<File[]>(
    []
  );
  const [selectedLessonForRecording, setSelectedLessonForRecording] = useState<
    string | null
  >(null);
  const [isCreatingRecording, setCreatingRecording] = useState<boolean>(false);
  const [fileRejectionErrors, setFileRejectionErrors] = useState<string[]>([]);
  const [selectedRecording, setSelectedRecording] =
    useState<RecordingProps | null>(null);
  const [isRecordingDetailsModalOpen, setIsRecordingDetailsModalOpen] =
    useState<boolean>(false);

  const handleRecordingFileDrop = (files: File[]) => {
    setSelectedRecordingFiles(files);
    setFileRejectionErrors([]); // Clear any previous errors when valid files are dropped
  };

  const handleRecordingFileReject = (fileRejections: any[]) => {
    const errors: string[] = [];

    fileRejections.forEach((rejection) => {
      rejection.errors.forEach((error: any) => {
        switch (error.code) {
          case "file-invalid-type":
            errors.push(
              `File "${rejection.file.name}" has an invalid format. Please upload MP4 files only.`
            );
            break;
          case "file-too-large":
            errors.push(
              `File "${rejection.file.name}" is too large. Maximum file size is 1GB.`
            );
            break;
          case "too-many-files":
            errors.push(`Too many files selected. Maximum 5 files allowed.`);
            break;
          default:
            errors.push(
              `File "${rejection.file.name}" was rejected: ${error.message}`
            );
        }
      });
    });

    setFileRejectionErrors(errors);
    DefaultErrorNotification(errors.join(" "));
  };

  const CreateNewRecording = async () => {
    if (!selectedLessonForRecording || selectedRecordingFiles.length === 0) {
      DefaultErrorNotification(
        "Please select a lesson and upload recording files"
      );
      return;
    } else {
      setCreatingRecording(true);
      try {
        const uploaded = await UploadFiles(selectedRecordingFiles, true);
        setCreatingRecording(false);
        if (uploaded && uploaded.status === HttpSuccessCodesObj.Created) {
          setCreatingRecording(true);
          const r = await PerformRequest({
            route: Endpoints.GetCreateRecording().route,
            method: Endpoints.GetCreateRecording().method,
            data: {
              courseID: courseID as string,
              lessonID: selectedLessonForRecording,
              files: uploaded.data.map((f) => f.path),
            },
            callback: () => setCreatingRecording(false),
          });
          setCreatingRecording(false);
          if (r) {
            if (r.status === HttpSuccessCodesObj.Created) {
              DefaultSuccessNotification("Recording created successfully!");
              reloadRecordings();
              setSelectedRecordingFiles([]);
              setSelectedLessonForRecording(null);
              setIsRecordingModalOpen(false);
            } else {
              DefaultErrorNotification("Failed to create recording");
            }
          }
        } else {
          setCreatingRecording(false);
          DefaultErrorNotification("Failed to upload recording files");
        }
      } catch (error) {
        setCreatingRecording(false);
        DefaultErrorNotification("Failed to create recording");
      }
    }
  };

  const handleCloseRecordingModal = () => {
    setIsRecordingModalOpen(false);
    setSelectedRecordingFiles([]);
    setSelectedLessonForRecording(null);
    setFileRejectionErrors([]);
  };

  const handleRecordingClick = (recording: RecordingProps) => {
    setSelectedRecording(recording);
    setIsRecordingDetailsModalOpen(true);
  };

  const handleCloseRecordingDetailsModal = () => {
    setIsRecordingDetailsModalOpen(false);
    setSelectedRecording(null);
  };

  const handleFileClick = (filePath: string) => {
    window.open(filePath, "_blank");
  };

  const gridProps: GridProps = {
    size: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 4,
      xl: 4,
    },
  };
  const textProps: TextProps = {
    fz: 13,
    className: "table-value",
  };

  return (
    <Container maxWidth="lg">
      <div className="single-course-container">
        {isLoadingCourse ? (
          <MegaLoader />
        ) : (
          <>
            <Text fw={700} fz={21} c={Colors.PurplePrimary}>
              {course?.data.name}
            </Text>
            <Text mb={10} maw={1200} fz={16}>
              {course?.data.description}
            </Text>

            <div className="flex-row align-center">
              <IconUserCircle
                color={
                  course?.data?.teacherID && course?.data?.teacherID?.length > 0
                    ? Colors.BluePrimary
                    : Colors.GraySecondary
                }
              />
              {course?.data?.teacherID &&
              course?.data?.teacherID?.length > 0 ? (
                <Text ml={4} fz={16} c={Colors.BluePrimary}>
                  {context?.profile?.id === course?.data?.teacherID && (
                    <>{context?.profile?.name}</>
                  )}
                </Text>
              ) : (
                <Text
                  fz={15}
                  c={Colors.OrangePrimary}
                  style={{
                    textDecoration: "underline",
                    textDecorationColor: Colors.OrangePrimary,
                    textDecorationThickness: 1,
                    textUnderlineOffset: 4,
                    textDecorationStyle: "dashed",
                  }}
                >
                  No teacher assigned
                </Text>
              )}
            </div>

            <Tabs
              mt={25}
              defaultValue="lessons"
              variant="default"
              color={Colors.PurplePrimary}
            >
              <Tabs.List grow>
                <Tabs.Tab
                  value="lessons"
                  leftSection={<IconNotebook size={19} />}
                >
                  Lessons
                </Tabs.Tab>
                <Tabs.Tab
                  value="students"
                  leftSection={<IconUsers size={19} />}
                >
                  Students
                </Tabs.Tab>
                <Tabs.Tab
                  value="recordings"
                  leftSection={<IconVideo size={19} />}
                >
                  Recordings
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="lessons">
                <div className="flex-row align-center">
                  <IconChevronRight size={19} color={Colors.PurplePrimary} />
                  <Text fw={600} my={10} c={Colors.PurplePrimary}>
                    Lessons
                  </Text>
                </div>
                <div className="flex-col width-100">
                  {isLoadingLessons ? (
                    <MegaLoader />
                  ) : (
                    <>
                      {lessons && lessons.data && lessons.data.length > 0 ? (
                        <Grid container spacing={2}>
                          {lessons.data.map((lesson) => (
                            <Grid {...gridProps} key={lesson.id}>
                              <Card
                                key={lesson.id}
                                shadow="sm"
                                padding="lg"
                                radius="md"
                                withBorder
                                mt="sm"
                                h={250}
                                className="lesson-card"
                              >
                                <Group justify="space-between" mb="xs">
                                  <Text
                                    fw={500}
                                    fz={15}
                                    c={Colors.PurplePrimary}
                                  >
                                    {lesson.title}
                                  </Text>
                                  <Badge
                                    color={Colors.PurplePrimary}
                                    variant="light"
                                  >
                                    {dayjs(lesson.createdAt).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </Badge>
                                </Group>

                                <Text
                                  size="sm"
                                  c={Colors.GrayPrimary}
                                  lineClamp={2}
                                >
                                  {getEllipsisFromString(
                                    lesson.description,
                                    100
                                  )}
                                </Text>

                                <Button
                                  variant="outline"
                                  color={Colors.PurplePrimary}
                                  fullWidth
                                  mt="md"
                                  radius="sm"
                                  leftSection={<IconEye size={16} />}
                                  onClick={() => {
                                    OpenLessonModal();
                                    setCurrentLesson(lesson);
                                  }}
                                >
                                  View Details
                                </Button>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <DisplayAlert
                          title="No lessons found"
                          children={
                            <Text>
                              No lessons found for this course. The Admin has
                              not added any lessons for this course yet.
                            </Text>
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              </Tabs.Panel>
              <Tabs.Panel value="students">
                <br />
                <DatagridDisplay
                  rows={students?.data ?? []}
                  columns={[
                    {
                      field: "name",
                      flex: 1,
                      headerName: "Name",
                      renderCell: (
                        params: GridRenderCellParams<StudentProfileProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text {...textProps}>{params.row.name}</Text>
                        </div>
                      ),
                    },
                    {
                      field: "email",
                      flex: 1,
                      headerName: "Email",
                      renderCell: (
                        params: GridRenderCellParams<StudentProfileProps>
                      ) => (
                        <CopyButton value={params.row.email}>
                          {({ copied, copy }) => (
                            <Tooltip
                              label={copied ? "Copied" : "Copy"}
                              withArrow
                              position="top"
                            >
                              <Text
                                {...textProps}
                                onClick={copy}
                                c={
                                  copied
                                    ? Colors.GreenPrimary
                                    : Colors.BluePrimary
                                }
                              >
                                {params.row.email}
                              </Text>
                            </Tooltip>
                          )}
                        </CopyButton>
                      ),
                    },
                    {
                      field: "id",
                      headerName: "Actions",
                      flex: 1,
                      renderCell: (
                        params: GridRenderCellParams<StudentProfileProps>
                      ) => (
                        <div className="flex-row align-center flex-1">
                          <ActionIcon
                            variant="outline"
                            color={Colors.PurplePrimary}
                            radius="sm"
                            onClick={() => {
                              setSelectedStudent(params.row);
                              openStudentModal();
                            }}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </div>
                      ),
                    },
                  ]}
                  isLoading={isLoadingStudents}
                />
              </Tabs.Panel>
              <Tabs.Panel value="recordings">
                <div className="flex-row align-center width-100 justify-between">
                  <div className="flex-row align-center">
                    <IconChevronRight size={19} color={Colors.PurplePrimary} />
                    <Text fw={600} my={10} c={Colors.PurplePrimary}>
                      Recordings
                    </Text>
                  </div>
                  <div className="flex-row align-center">
                    <ActionIcon
                      size="lg"
                      color={Colors.PurplePrimary}
                      disabled={isLoadingRecordings}
                      onClick={() => setIsRecordingModalOpen(true)}
                    >
                      <IconPlus size={18} />
                    </ActionIcon>
                    <ActionIcon
                      ml={10}
                      variant="outline"
                      size="lg"
                      color={Colors.PurplePrimary}
                      onClick={reloadRecordings}
                      loading={isLoadingRecordings}
                    >
                      <IconReload size={18} />
                    </ActionIcon>
                  </div>
                </div>
                {isLoadingRecordings ? (
                  <>
                    <MegaLoader />
                  </>
                ) : (
                  <>
                    {recordings?.data && recordings.data.length > 0 ? (
                      <DatagridDisplay
                        rows={recordings.data}
                        isLoading={isLoadingRecordings}
                        onRowClick={(params) =>
                          handleRecordingClick(params.row)
                        }
                        columns={[
                          {
                            field: "lessonID",
                            flex: 1,
                            headerName: "Lesson",
                            renderCell: (
                              params: GridRenderCellParams<RecordingProps>
                            ) => (
                              <div className="flex-row flex-1 align-center">
                                <Text fz={13} className="table-value">
                                  {
                                    lessons?.data.find(
                                      (lesson) =>
                                        lesson.id === params.row.lessonID
                                    )?.title
                                  }
                                </Text>
                              </div>
                            ),
                          },
                          {
                            field: "createdAt",
                            flex: 1,
                            headerName: "Creation Date",
                            renderCell: (
                              params: GridRenderCellParams<RecordingProps>
                            ) => (
                              <div className="flex-row flex-1 align-center">
                                <Text fz={13} className="table-value">
                                  {dayjs(params.row.createdAt).format(
                                    "DD MMM YYYY"
                                  )}
                                </Text>
                              </div>
                            ),
                          },
                          {
                            field: "lastUpdatedAt",
                            flex: 1,
                            headerName: "Last Updated",
                            renderCell: (
                              params: GridRenderCellParams<RecordingProps>
                            ) => (
                              <div className="flex-row flex-1 align-center">
                                <Text fz={13} className="table-value">
                                  {dayjs(params.row.lastUpdatedAt).format(
                                    "DD MMM YYYY, hh:mm A"
                                  )}
                                </Text>
                              </div>
                            ),
                          },
                        ]}
                      />
                    ) : (
                      <div className="flex-col width-100">
                        <DisplayAlert
                          title="No Recordings Found"
                          children={
                            <div className="flex-col">
                              <Text>
                                No recordings found for this course. You can
                                start creating recordings by clicking the button
                                above.
                              </Text>
                              <Button
                                w={230}
                                mt={10}
                                color={Colors.PurplePrimary}
                                leftSection={<IconPlus size={16} />}
                                onClick={() => setIsRecordingModalOpen(true)}
                              >
                                Create Recording
                              </Button>
                            </div>
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </Tabs.Panel>
            </Tabs>
          </>
        )}
      </div>
      <Modal
        centered
        size="lg"
        title={
          <Group gap={8}>
            <IconVideo size={20} />
            <Text fw={600}>Create New Recording</Text>
          </Group>
        }
        opened={isRecordingModalOpen}
        onClose={handleCloseRecordingModal}
      >
        <div className="flex-col">
          <div className="recording-form-header">
            <Group justify="space-between" mb={20}>
              <div className="flex-row align-center">
                <IconVideo size={24} color={Colors.PurplePrimary} />
                <Text fw={600} fz={20} ml={10} c={Colors.PurplePrimary}>
                  Upload Recording Files
                </Text>
              </div>
            </Group>
          </div>

          <Divider my="md" />

          <div className="recording-form-content">
            <div className="lesson-selection">
              <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                Select Lesson
              </Text>
              <Select
                placeholder="Choose a lesson for this recording"
                data={lessons?.data?.map((lesson) => ({
                  value: lesson.id,
                  label: lesson.title,
                }))}
                value={selectedLessonForRecording}
                onChange={(value) => setSelectedLessonForRecording(value)}
                clearable
                radius="md"
                mb={20}
              />
            </div>

            <div className="file-upload">
              <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                Upload Recording Files
              </Text>
              <Dropzone
                onDrop={handleRecordingFileDrop}
                onReject={handleRecordingFileReject}
                accept={[MIME_TYPES.mp4]}
                maxFiles={5}
                maxSize={1024 * 1024 * 1024}
                radius="md"
                style={{
                  border: "2px dashed #dee2e6",
                  background: "#f8f9fa",
                }}
              >
                <Group
                  justify="center"
                  gap="xl"
                  style={{ minHeight: 80, pointerEvents: "none" }}
                >
                  <Dropzone.Accept>
                    <IconUpload
                      style={{
                        width: 32,
                        height: 32,
                        color: "var(--mantine-color-blue-6)",
                      }}
                      stroke={1.5}
                    />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconX
                      style={{
                        width: 32,
                        height: 32,
                        color: "var(--mantine-color-red-6)",
                      }}
                      stroke={1.5}
                    />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <IconVideo
                      style={{
                        width: 32,
                        height: 32,
                        color: "var(--mantine-color-dimmed)",
                      }}
                      stroke={1.5}
                    />
                  </Dropzone.Idle>
                  <div>
                    <Text size="xl" inline>
                      Drag recording files here or click to select
                    </Text>
                    <Text size="sm" c="dimmed" inline mt={7}>
                      Upload <strong>MP4</strong> files ( 1GB max)
                    </Text>
                  </div>
                </Group>
              </Dropzone>

              {fileRejectionErrors.length > 0 && (
                <div className="file-errors mt={10}">
                  <Card
                    withBorder
                    p="md"
                    radius="md"
                    bg="red.0"
                    style={{ borderColor: "var(--mantine-color-red-3)" }}
                  >
                    <Group mb={10}>
                      <IconX size={16} color="var(--mantine-color-red-6)" />
                      <Text fw={500} fz={14} c="red">
                        File Upload Errors
                      </Text>
                    </Group>
                    <div className="flex-col">
                      {fileRejectionErrors.map((error, index) => (
                        <Text key={index} fz={13} c="red" mb={5}>
                          • {error}
                        </Text>
                      ))}
                    </div>
                  </Card>
                </div>
              )}
            </div>

            {selectedRecordingFiles?.length > 0 && (
              <div className="selected-files mt={20}">
                <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                  Selected Files
                </Text>
                <Card withBorder p="md" radius="md" bg="gray.0">
                  <div className="flex-col">
                    {selectedRecordingFiles?.map((file, index) => (
                      <div key={index} className="flex-row align-center mb={8}">
                        <Badge color="green" variant="light" size="sm" mr={8}>
                          {index + 1}
                        </Badge>
                        <Text fz={14} c={Colors.GrayPrimary}>
                          {file.name} ({(file.size / 1024 / 1024).toFixed(2)}{" "}
                          MB)
                        </Text>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}
            <br />
            <div className="flex-row align-center justify-between width-100">
              <Button
                w="48%"
                variant="outline"
                color="gray"
                onClick={handleCloseRecordingModal}
                leftSection={<IconX size={16} />}
              >
                Cancel
              </Button>
              <Button
                w="48%"
                color={Colors.PurplePrimary}
                onClick={CreateNewRecording}
                loading={isCreatingRecording}
                disabled={
                  !selectedLessonForRecording ||
                  selectedRecordingFiles.length === 0
                }
                leftSection={<IconCheck size={16} />}
              >
                Create Recording
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        centered
        size="lg"
        title={
          <Group gap={8}>
            <IconVideo size={20} />
            <Text fw={600}>Recording Details</Text>
          </Group>
        }
        opened={isRecordingDetailsModalOpen}
        onClose={handleCloseRecordingDetailsModal}
      >
        {selectedRecording && (
          <div className="flex-col">
            <div className="recording-details-header">
              <Group justify="space-between" mb={20}>
                <div className="flex-row align-center">
                  <IconVideo size={24} color={Colors.PurplePrimary} />
                  <Text fw={600} fz={20} ml={10} c={Colors.PurplePrimary}>
                    Recording Details
                  </Text>
                </div>
                <Badge color={Colors.PurplePrimary} variant="light" size="lg">
                  Created{" "}
                  {dayjs(selectedRecording.createdAt).format("DD MMM YYYY")}
                </Badge>
              </Group>
            </div>

            <Divider my="md" />

            <div className="recording-details-content">
              <div className="recording-metadata">
                <Card withBorder p="md" radius="md" bg="gray.0">
                  <Group mb={15}>
                    <IconVideo size={20} color={Colors.PurplePrimary} />
                    <Text fw={500} fz={16} c={Colors.PurplePrimary}>
                      Recording Details
                    </Text>
                  </Group>
                  <Group gap="xl">
                    <div className="flex-col">
                      <Text fz={12} c="dimmed" mb={5}>
                        Lesson
                      </Text>
                      <Text fw={500} fz={14}>
                        {
                          lessons?.data.find(
                            (lesson) => lesson.id === selectedRecording.lessonID
                          )?.title
                        }
                      </Text>
                    </div>
                    <div className="flex-col">
                      <Text fz={12} c="dimmed" mb={5}>
                        Creation Date
                      </Text>
                      <Text fw={500} fz={14}>
                        {dayjs(selectedRecording.createdAt).format(
                          "DD MMM YYYY"
                        )}
                      </Text>
                    </div>
                    <div className="flex-col">
                      <Text fz={12} c="dimmed" mb={5}>
                        Last Updated
                      </Text>
                      <Text fw={500} fz={14}>
                        {dayjs(selectedRecording.lastUpdatedAt).format(
                          "DD MMM YYYY, hh:mm A"
                        )}
                      </Text>
                    </div>
                  </Group>
                </Card>
              </div>

              <div className="recording-files mt={20}">
                <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                  Files
                </Text>
                <Card withBorder p="md" radius="md" bg="gray.0">
                  <div className="flex-col">
                    {selectedRecording.files.map((filePath, index) => (
                      <div key={index} className="flex-row align-center mb={8}">
                        <Badge color="blue" variant="light" size="sm" mr={8}>
                          {index + 1}
                        </Badge>
                        <Text fz={14} c={Colors.GrayPrimary}>
                          {filePath.split("/").pop() || filePath}
                        </Text>
                        <ActionIcon
                          size="sm"
                          color="blue"
                          ml={10}
                          onClick={() => handleFileClick(filePath)}
                          variant="subtle"
                        >
                          <IconPhoto size={16} />
                        </ActionIcon>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              <Group justify="space-between" mt={30}>
                <Button
                  variant="outline"
                  color="gray"
                  onClick={handleCloseRecordingDetailsModal}
                  leftSection={<IconX size={16} />}
                >
                  Close
                </Button>
              </Group>
            </div>
          </div>
        )}
      </Modal>
      <Modal
        centered
        title="Lesson Details"
        opened={isLessonModalOpen}
        size="xl"
        onClose={() => {
          CloseLessonModal();
          setCurrentLesson(undefined);
        }}
      >
        {currentLesson && (
          <>
            <Table variant="vertical" layout="fixed" withTableBorder>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th w={160}>Title</Table.Th>
                  <Table.Td>{currentLesson.title}</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Th>Description</Table.Th>
                  <Table.Td>
                    {getEllipsisFromString(currentLesson.description, 100)}
                  </Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Th>Created at</Table.Th>
                  <Table.Td>
                    {dayjs(currentLesson.createdAt).format("DD/MM/YYYY")}{" "}
                    {dayjs(currentLesson.createdAt).format("hh:mmA")}
                  </Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Th>Last updated at</Table.Th>
                  <Table.Td>
                    {dayjs(currentLesson.updatedAt).format("DD/MM/YYYY")}{" "}
                    {dayjs(currentLesson.updatedAt).format("hh:mmA")}
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
            <Divider my={10} />
            <div className="flex-row align-center">
              <Link to={`/d/ongoing/${currentLesson.id}`}>
                <Button
                  leftSection={<IconPlayerPlay size={18} />}
                  color={Colors.PurplePrimary}
                >
                  Start Lesson
                </Button>
                &nbsp; &nbsp;
              </Link>
              <Link to={`/d/lesson/assessments/${currentLesson.id}`}>
                <Button
                  variant="outline"
                  color={Colors.PurplePrimary}
                  leftSection={<IconTerminal2 size={18} />}
                >
                  Assessments
                </Button>
              </Link>
              &nbsp; &nbsp;
              <Link to={`/d/lesson/materials/${currentLesson.id}`}>
                <Button
                  variant="outline"
                  color={Colors.PurplePrimary}
                  leftSection={<IconFiles size={18} />}
                >
                  Materials
                </Button>
              </Link>
            </div>
          </>
        )}
      </Modal>
      <Modal
        centered
        title="Student Details"
        opened={isStudentModalOpen}
        size="lg"
        onClose={() => {
          closeStudentModal();
          setSelectedStudent(undefined);
        }}
      >
        {selectedStudent && (
          <Stack gap="md">
            <Group>
              <Avatar size="xl" radius="xl" color={Colors.PurplePrimary}>
                {selectedStudent.name.charAt(0).toUpperCase()}
              </Avatar>
              <div>
                <Text fw={700} fz={24} c={Colors.PurplePrimary}>
                  {selectedStudent.name}
                </Text>
                <Text fz={16} c={Colors.GrayPrimary}>
                  {selectedStudent.email}
                </Text>
              </div>
            </Group>

            <Divider />

            <Table>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th w={160}>Student ID</Table.Th>
                  <Table.Td>
                    <CopyButton value={selectedStudent.id}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          withArrow
                          position="top"
                        >
                          <Text
                            onClick={copy}
                            c={
                              copied ? Colors.GreenPrimary : Colors.BluePrimary
                            }
                            style={{ cursor: "pointer" }}
                          >
                            {selectedStudent.id}
                          </Text>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Td>
                    <CopyButton value={selectedStudent.email}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          withArrow
                          position="top"
                        >
                          <Text
                            onClick={copy}
                            c={
                              copied ? Colors.GreenPrimary : Colors.BluePrimary
                            }
                            style={{ cursor: "pointer" }}
                          >
                            {selectedStudent.email}
                          </Text>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>

            <Divider />

            <Text fw={600} fz={18} c={Colors.PurplePrimary}>
              Assessment Links
            </Text>
            <Group>
              <Button
                variant="outline"
                color={Colors.PurplePrimary}
                leftSection={<IconClipboardCheck size={18} />}
                onClick={openAssessmentHistoryModal}
              >
                View Assessments
              </Button>

              <Button
                variant="outline"
                color={Colors.PurplePrimary}
                leftSection={<IconChartBar size={18} />}
                onClick={openStudentPerformanceModal}
              >
                Progress Report
              </Button>
            </Group>

            <Group justify="flex-end" mt="md">
              <Button
                variant="outline"
                color={Colors.PurplePrimary}
                onClick={() => {
                  closeStudentModal();
                  setSelectedStudent(undefined);
                }}
              >
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Student Performance Modal */}
      <Modal
        centered
        title="Student Performance"
        opened={isStudentPerformanceModalOpen}
        size="xl"
        onClose={closeStudentPerformanceModal}
        padding={0}
        styles={{ body: { padding: 0 } }}
      >
        {selectedStudent && courseID && (
          <StudentPerformance
            studentID={selectedStudent.id}
            courseID={courseID}
          />
        )}
      </Modal>

      <Modal
        centered
        title="Assessment History"
        opened={isAssessmentHistoryModalOpen}
        size="xl"
        onClose={closeAssessmentHistoryModal}
      >
        {isLoadingStudentAssessmentHistory ? (
          <MegaLoader />
        ) : (
          <Stack gap="md">
            <ScrollArea h={400}>
              {studentAssessmentHistory?.attempts &&
              studentAssessmentHistory.attempts.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Assessment</Table.Th>
                      <Table.Th>Score</Table.Th>
                      <Table.Th>Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {studentAssessmentHistory.attempts.map((attempt) => (
                      <Table.Tr key={attempt.studentID}>
                        <Table.Td>
                          <Text fw={500}>
                            {
                              studentAssessmentHistory.assessments.find((a) =>
                                a.attempts.some(
                                  (at) => at.studentID === attempt.studentID
                                )
                              )?.title
                            }
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={
                              attempt.score >= 70
                                ? Colors.GreenPrimary
                                : Colors.RedPrimary
                            }
                          >
                            {attempt.score}%
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {dayjs(attempt.date).format("DD/MM/YYYY hh:mm A")}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  No attempt history found for this student.
                </Text>
              )}
            </ScrollArea>
          </Stack>
        )}
      </Modal>
    </Container>
  );
};

export default SingleCourse;
